// auth.ts - Authentication module for NOVA widget
// Handles OAuth flows, wallet connections, and session linking

import type { NovaError } from './types';

// ═══════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════

export type AuthProvider = 'discord' | 'google' | 'telegram' | 'stacks' | 'bitcoin';
export type AuthProviderType = 'oauth' | 'widget' | 'wallet';

export interface AuthProviderInfo {
  id: AuthProvider;
  name: string;
  type: AuthProviderType;
  icon: string;
}

export interface AuthUser {
  id: number;
  platform: AuthProvider;
  platformUserId: string;
  displayName: string;
  avatar?: string;
  isPremium: boolean;
  /** The person's AI allowance, as percentages only. */
  usage?: UsageStatus | null;
}

/** One rolling usage window: a whole percentage, 0 to 100 (100 = used up). */
export interface UsageWindow {
  percent: number;
}

/**
 * The personal AI allowance as the server reports it: a session window (the
 * last 5 hours) and a week. There are no counts and no amounts.
 */
export interface UsageStatus {
  session: UsageWindow;
  week: UsageWindow;
  limited: boolean;
  limited_by: 'session' | 'week' | null;
  /** ISO time the allowance has room again; null while it has room. */
  available_at: string | null;
}

export interface AuthStatus {
  authenticated: boolean;
  user?: AuthUser;
  anonymousMessagesUsed?: number;
  anonymousMessagesLimit?: number;
  loginRequired?: boolean;
}

export interface AuthInitResponse {
  success: boolean;
  provider: AuthProvider;
  authUrl?: string;         // For OAuth (Discord, Google)
  widgetConfig?: {          // For Telegram
    botUsername: string;
    callbackUrl: string;
  };
  challenge?: string;       // For wallet signature
  nonce?: string;
  expirationTime?: string;
  state: string;
  type?: 'oauth' | 'telegram_widget' | 'wallet_signature';
  config?: Record<string, unknown>;
}

export interface AuthCallbackResponse {
  success: boolean;
  user: AuthUser;
  sessionLinked: boolean;
}

export interface TelegramLoginData {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

export interface WalletSignatureData {
  message: string;
  signature: string;
  publicKey?: string;
  address: string;
}

// ═══════════════════════════════════════════════════════════════
// Auth Client
// ═══════════════════════════════════════════════════════════════

export class NovaAuthClient {
  private apiKey: string;
  private apiUrl: string;
  private sessionId: string | null = null;

  // Callback handlers
  public onAuthSuccess?: (user: AuthUser) => void;
  public onAuthError?: (error: NovaError) => void;
  public onLogout?: () => void;

  constructor(apiKey: string, apiUrl: string = 'https://nova.zeroauthoritydao.com') {
    this.apiKey = apiKey;
    this.apiUrl = apiUrl.replace(/\/$/, '');
  }

  setSessionId(sessionId: string): void {
    this.sessionId = sessionId;
  }

  private async fetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.apiUrl}/api/widget/auth${endpoint}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'x-api-key': this.apiKey,
      ...(this.sessionId && { 'x-session-id': this.sessionId }),
      ...(options.headers as Record<string, string>),
    };

    const response = await fetch(url, { ...options, headers });
    const data = await response.json();

    if (!response.ok) {
      const error: NovaError = {
        code: data.error || 'auth_error',
        message: data.message || 'Authentication failed',
      };
      throw error;
    }

    return data as T;
  }

  /**
   * Get available authentication providers
   */
  async getProviders(): Promise<AuthProviderInfo[]> {
    const response = await this.fetch<{ success: boolean; providers: AuthProviderInfo[] }>('/providers');
    return response.providers;
  }

  /**
   * Get current authentication status
   */
  async getStatus(): Promise<AuthStatus> {
    const response = await this.fetch<{ success: boolean } & AuthStatus>('/status');
    return {
      authenticated: response.authenticated,
      user: response.user,
      anonymousMessagesUsed: response.anonymousMessagesUsed,
      anonymousMessagesLimit: response.anonymousMessagesLimit,
      loginRequired: response.loginRequired,
    };
  }

  /**
   * Initialize OAuth flow for a provider
   */
  async initAuth(provider: AuthProvider, options: { domain?: string } = {}): Promise<AuthInitResponse> {
    if (!this.sessionId) {
      throw { code: 'no_session', message: 'Session not initialized' } as NovaError;
    }

    return this.fetch<AuthInitResponse>('/init', {
      method: 'POST',
      body: JSON.stringify({
        provider,
        sessionId: this.sessionId,
        domain: options.domain || window.location.hostname,
      }),
    });
  }

  /**
   * Handle OAuth callback (for popup flow)
   */
  async handleOAuthCallback(provider: AuthProvider, code: string, state: string): Promise<AuthCallbackResponse> {
    return this.fetch<AuthCallbackResponse>(`/callback/${provider}`, {
      method: 'POST',
      body: JSON.stringify({ code, state }),
    });
  }

  /**
   * Handle Telegram Login Widget callback
   */
  async handleTelegramCallback(loginData: TelegramLoginData, state: string): Promise<AuthCallbackResponse> {
    return this.fetch<AuthCallbackResponse>('/callback/telegram', {
      method: 'POST',
      body: JSON.stringify({ ...loginData, state }),
    });
  }

  /**
   * Handle wallet signature callback
   */
  async handleWalletCallback(
    provider: 'stacks' | 'bitcoin',
    signatureData: WalletSignatureData,
    state: string
  ): Promise<AuthCallbackResponse> {
    return this.fetch<AuthCallbackResponse>(`/callback/${provider}`, {
      method: 'POST',
      body: JSON.stringify({ ...signatureData, state }),
    });
  }

  /**
   * Logout / disconnect account
   */
  async logout(): Promise<boolean> {
    try {
      await this.fetch<{ success: boolean }>('/logout', { method: 'POST' });
      this.onLogout?.();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Start OAuth popup flow
   * Returns the authenticated user directly (backend handles the token exchange)
   */
  startOAuthPopup(authUrl: string, provider: AuthProvider): Promise<AuthUser> {
    return new Promise((resolve, reject) => {
      const width = 500;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        authUrl,
        `nova_${provider}_login`,
        `width=${width},height=${height},left=${left},top=${top},popup=1`
      );

      if (!popup) {
        reject({ code: 'popup_blocked', message: 'Popup was blocked. Please allow popups for this site.' } as NovaError);
        return;
      }

      let checkClosedInterval: ReturnType<typeof setInterval>;

      // Listen for callback message from popup
      const handleMessage = (event: MessageEvent) => {
        // Check for our auth callback message
        if (event.data?.type === 'nova-auth-callback') {
          window.removeEventListener('message', handleMessage);
          clearInterval(checkClosedInterval);

          if (event.data.success && event.data.user) {
            resolve(event.data.user as AuthUser);
          } else {
            reject({
              code: event.data.error || 'auth_failed',
              message: event.data.message || 'Authentication failed'
            } as NovaError);
          }
        }
      };

      window.addEventListener('message', handleMessage);

      // Check if popup was closed without completing auth
      checkClosedInterval = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosedInterval);
          window.removeEventListener('message', handleMessage);
          reject({ code: 'popup_closed', message: 'Login cancelled' } as NovaError);
        }
      }, 500);
    });
  }
}

// ═══════════════════════════════════════════════════════════════
// Provider Icons (SVG data URIs)
// ═══════════════════════════════════════════════════════════════

export const PROVIDER_ICONS: Record<AuthProvider, string> = {
  discord: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>`,

  google: `<svg viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>`,

  telegram: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>`,

  stacks: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 0L1.5 6v12L12 24l10.5-6V6L12 0zm0 2.37l8.13 4.69v9.38L12 21.13l-8.13-4.69V6.56L12 2.37zm0 3.38L6.38 9v6L12 18.25 17.63 15V9L12 5.75z"/></svg>`,

  bitcoin: `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M23.638 14.904c-1.602 6.43-8.113 10.34-14.542 8.736C2.67 22.05-1.244 15.525.362 9.105 1.962 2.67 8.475-1.243 14.9.358c6.43 1.605 10.342 8.115 8.738 14.546zm-6.35-4.613c.24-1.59-.974-2.45-2.64-3.03l.54-2.153-1.315-.33-.525 2.107c-.345-.087-.705-.167-1.064-.25l.526-2.127-1.32-.33-.54 2.165c-.285-.067-.565-.132-.84-.2l-1.815-.45-.35 1.407s.974.225.955.238c.535.136.63.486.615.766l-1.477 5.92c-.075.18-.24.45-.614.35.015.02-.96-.24-.96-.24l-.66 1.51 1.71.426.93.242-.54 2.19 1.32.327.54-2.17c.36.1.705.19 1.05.273l-.51 2.154 1.32.33.545-2.19c2.24.427 3.93.257 4.64-1.774.57-1.637-.03-2.58-1.217-3.196.854-.193 1.5-.76 1.68-1.93h.01zm-3.01 4.22c-.404 1.64-3.157.75-4.05.53l.72-2.9c.896.23 3.757.67 3.33 2.37zm.41-4.24c-.37 1.49-2.662.735-3.405.55l.654-2.64c.744.18 3.137.52 2.75 2.084v.006z"/></svg>`,
};

// ═══════════════════════════════════════════════════════════════
// Login Modal Component
// ═══════════════════════════════════════════════════════════════

export interface LoginModalOptions {
  authClient: NovaAuthClient;
  onSuccess?: (user: AuthUser) => void;
  onError?: (error: NovaError) => void;
  onClose?: () => void;
}

export class NovaLoginModal {
  private authClient: NovaAuthClient;
  private modal: HTMLElement | null = null;
  private isOpen: boolean = false;
  private state: string | null = null;
  private currentProvider: AuthProvider | null = null;

  public onSuccess?: (user: AuthUser) => void;
  public onError?: (error: NovaError) => void;
  public onClose?: () => void;

  constructor(options: LoginModalOptions) {
    this.authClient = options.authClient;
    this.onSuccess = options.onSuccess;
    this.onError = options.onError;
    this.onClose = options.onClose;
  }

  /**
   * Open the login modal
   */
  async open(): Promise<void> {
    if (this.isOpen) return;

    // Create modal if not exists
    if (!this.modal) {
      this.createModal();
    }

    // Load providers and render
    await this.renderProviders();

    // Show modal
    this.modal!.classList.add('nova-login--open');
    this.isOpen = true;
  }

  /**
   * Close the login modal
   */
  close(): void {
    if (!this.isOpen || !this.modal) return;

    this.modal.classList.remove('nova-login--open');
    this.isOpen = false;
    this.state = null;
    this.currentProvider = null;
    this.onClose?.();
  }

  /**
   * Create the modal DOM structure
   */
  private createModal(): void {
    this.modal = document.createElement('div');
    this.modal.className = 'nova-login';
    this.modal.innerHTML = `
      <div class="nova-login__backdrop"></div>
      <div class="nova-login__container">
        <div class="nova-login__header">
          <h2 class="nova-login__title">Connect Account</h2>
          <button class="nova-login__close" aria-label="Close">&times;</button>
        </div>
        <div class="nova-login__content">
          <p class="nova-login__subtitle">Sign in to unlock full features and keep your conversation history.</p>
          <div class="nova-login__providers"></div>
          <div class="nova-login__divider"><span>or connect wallet</span></div>
          <div class="nova-login__wallets"></div>
        </div>
        <div class="nova-login__footer">
          <p>By signing in, you agree to our Terms of Service and Privacy Policy.</p>
        </div>
      </div>
    `;

    // Bind events
    this.modal.querySelector('.nova-login__backdrop')?.addEventListener('click', () => this.close());
    this.modal.querySelector('.nova-login__close')?.addEventListener('click', () => this.close());

    // Append to body
    document.body.appendChild(this.modal);
  }

  /**
   * Render provider buttons
   */
  private async renderProviders(): Promise<void> {
    if (!this.modal) return;

    const providersContainer = this.modal.querySelector('.nova-login__providers');
    const walletsContainer = this.modal.querySelector('.nova-login__wallets');

    if (!providersContainer || !walletsContainer) return;

    try {
      const providers = await this.authClient.getProviders();

      // Separate OAuth providers from wallets
      const oauthProviders = providers.filter(p => p.type === 'oauth' || p.type === 'widget');
      const walletProviders = providers.filter(p => p.type === 'wallet');

      // Render OAuth providers
      providersContainer.innerHTML = oauthProviders.map(provider => `
        <button class="nova-login__btn nova-login__btn--${provider.id}" data-provider="${provider.id}">
          <span class="nova-login__btn-icon">${PROVIDER_ICONS[provider.id]}</span>
          <span class="nova-login__btn-text">Continue with ${provider.name}</span>
        </button>
      `).join('');

      // Render wallet providers
      if (walletProviders.length > 0) {
        walletsContainer.innerHTML = walletProviders.map(provider => `
          <button class="nova-login__btn nova-login__btn--${provider.id}" data-provider="${provider.id}">
            <span class="nova-login__btn-icon">${PROVIDER_ICONS[provider.id]}</span>
            <span class="nova-login__btn-text">Connect ${provider.name}</span>
          </button>
        `).join('');
      } else {
        // Hide wallet section if no wallet providers
        this.modal.querySelector('.nova-login__divider')?.classList.add('nova-login__divider--hidden');
        walletsContainer.classList.add('nova-login__wallets--hidden');
      }

      // Bind click events
      this.modal.querySelectorAll('.nova-login__btn[data-provider]').forEach(btn => {
        btn.addEventListener('click', async (e) => {
          const target = e.currentTarget as HTMLElement;
          const provider = target.dataset.provider as AuthProvider;
          await this.handleProviderClick(provider);
        });
      });

    } catch (error) {
      providersContainer.innerHTML = `
        <div class="nova-login__error">
          Failed to load login options. Please try again.
        </div>
      `;
    }
  }

  /**
   * Handle provider button click
   */
  private async handleProviderClick(provider: AuthProvider): Promise<void> {
    this.currentProvider = provider;
    this.setLoading(provider, true);

    try {
      const initResponse = await this.authClient.initAuth(provider);
      this.state = initResponse.state;

      if (initResponse.type === 'oauth' || provider === 'discord' || provider === 'google' || provider === 'telegram') {
        // OAuth / Telegram popup flow
        await this.handleOAuthFlow(provider, initResponse.authUrl!);
      } else if (initResponse.type === 'wallet_signature') {
        // Wallet signature flow
        await this.handleWalletFlow(provider, initResponse);
      }

    } catch (error) {
      this.onError?.(error as NovaError);
      this.setLoading(provider, false);
    }
  }

  /**
   * Handle OAuth popup flow
   */
  private async handleOAuthFlow(provider: AuthProvider, authUrl: string): Promise<void> {
    try {
      // The popup flow now returns the user directly (backend handles token exchange)
      const user = await this.authClient.startOAuthPopup(authUrl, provider);

      this.setLoading(provider, false);
      this.onSuccess?.(user);
      this.close();

    } catch (error) {
      this.setLoading(provider, false);
      const novaError = error as NovaError;
      if (novaError.code !== 'popup_closed') {
        this.onError?.(novaError);
      }
    }
  }

  /**
   * Handle wallet signature flow
   */
  private async handleWalletFlow(provider: 'stacks' | 'bitcoin', initResponse: AuthInitResponse): Promise<void> {
    // This would integrate with @stacks/connect or sats-connect
    // For now, show a message that wallet connection is in progress
    const message = initResponse.challenge!;

    try {
      let signatureData: WalletSignatureData;

      if (provider === 'stacks') {
        signatureData = await this.signWithStacks(message);
      } else {
        signatureData = await this.signWithBitcoin(message);
      }

      const response = await this.authClient.handleWalletCallback(provider, signatureData, this.state!);

      this.setLoading(provider, false);
      this.onSuccess?.(response.user);
      this.close();

    } catch (error) {
      this.setLoading(provider, false);
      this.onError?.(error as NovaError);
    }
  }

  /**
   * Sign message with Stacks wallet
   */
  private async signWithStacks(message: string): Promise<WalletSignatureData> {
    // This requires @stacks/connect to be loaded
    // The widget should include it via CDN or the user should have it installed
    if (typeof window !== 'undefined' && (window as any).StacksProvider) {
      const provider = (window as any).StacksProvider;

      // Get the user's STX address (stx_signMessage doesn't return it)
      const accountsResponse = await provider.request('stx_getAddresses', {});
      const stxAccount = accountsResponse.result.addresses.find(
        (a: any) => a.symbol === 'STX'
      );

      if (!stxAccount) {
        throw { code: 'no_stx_address', message: 'No STX address found in wallet.' } as NovaError;
      }

      // Sign the challenge message (response is JSON-RPC: { result: { signature, publicKey } })
      const signResponse = await provider.request('stx_signMessage', { message });

      return {
        message,
        signature: signResponse.result.signature,
        publicKey: signResponse.result.publicKey,
        address: stxAccount.address,
      };
    }

    throw { code: 'wallet_not_found', message: 'Stacks wallet not found. Please install Leather or Xverse.' } as NovaError;
  }

  /**
   * Sign message with Bitcoin wallet
   */
  private async signWithBitcoin(message: string): Promise<WalletSignatureData> {
    // This requires sats-connect or similar to be loaded
    if (typeof window !== 'undefined' && (window as any).btc) {
      const btc = (window as any).btc;
      const response = await btc.request('signMessage', { message });
      return {
        message,
        signature: response.signature,
        address: response.address,
      };
    }

    throw { code: 'wallet_not_found', message: 'Bitcoin wallet not found. Please install Leather, Xverse, or Unisat.' } as NovaError;
  }

  /**
   * Set loading state on a provider button
   */
  private setLoading(provider: AuthProvider, loading: boolean): void {
    const btn = this.modal?.querySelector(`.nova-login__btn--${provider}`);
    if (btn) {
      if (loading) {
        btn.classList.add('nova-login__btn--loading');
        btn.setAttribute('disabled', 'true');
      } else {
        btn.classList.remove('nova-login__btn--loading');
        btn.removeAttribute('disabled');
      }
    }
  }

  /**
   * Destroy the modal
   */
  destroy(): void {
    this.modal?.remove();
    this.modal = null;
    this.isOpen = false;
  }
}
